import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Youtube, Save, PhoneCall } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";

const STATUS_STYLES = {
  new: "bg-sky-light text-sky-hover",
  contacted: "bg-lemon/40 text-ink",
  scheduled: "bg-leaf/15 text-leaf",
  closed: "bg-cloud text-stone",
};
const STATUSES = ["new", "contacted", "scheduled", "closed"];

export default function AdminDemos() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ytUrl, setYtUrl] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: reqs }, { data: cfg }] = await Promise.all([
        api.get("/admin/demo-requests"),
        api.get("/site-config"),
      ]);
      setRequests(reqs || []);
      setYtUrl(cfg?.demo_youtube_url || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (r, status) => {
    try {
      await api.put(`/admin/demo-requests/${r.id}/status`, { status });
      toast.success(`Marked as ${status}`);
      setRequests((prev) => prev.map((x) => (x.id === r.id ? { ...x, status } : x)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    setSavingCfg(true);
    try {
      await api.put("/admin/site-config", { demo_youtube_url: ytUrl });
      toast.success("Landing page video updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSavingCfg(false); }
  };

  return (
    <AppLayout variant="admin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-6">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink">Demo requests</h1>
          <p className="text-stone mt-1">Parents who booked a free demo class from the landing page.</p>
        </div>

        <form onSubmit={saveConfig} className="mb-8 p-5 bg-white rounded-3xl card-tactile" data-testid="demo-video-config">
          <div className="flex items-center gap-2 mb-3">
            <Youtube className="w-5 h-5 text-watermelon" />
            <h2 className="font-heading text-lg font-bold text-ink">Landing page demo video</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="demo-video-url-input"
              value={ytUrl}
              onChange={(e) => setYtUrl(e.target.value)}
              placeholder="Paste a YouTube link (https://youtube.com/watch?v=…)"
              className="flex-1 h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none text-sm font-medium"
            />
            <button data-testid="demo-video-save" type="submit" disabled={savingCfg} className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70">
              <Save className="w-4 h-4" /> {savingCfg ? "Saving…" : "Save"}
            </button>
          </div>
          <p className="text-xs text-stone mt-2">Leave blank to show a "coming soon" placeholder on the About page.</p>
        </form>

        <div className="bg-white rounded-3xl card-tactile overflow-hidden">
          {loading ? (
            <div className="p-8 text-stone">Loading…</div>
          ) : requests.length === 0 ? (
            <div className="p-8 text-stone text-center">No demo requests yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-canvas">
                  <tr className="text-left text-xs uppercase tracking-wider text-stone">
                    <th className="p-4 font-bold">Child</th>
                    <th className="p-4 font-bold">Parent email</th>
                    <th className="p-4 font-bold">WhatsApp</th>
                    <th className="p-4 font-bold">Slot</th>
                    <th className="p-4 font-bold">Location</th>
                    <th className="p-4 font-bold">Requested</th>
                    <th className="p-4 font-bold">Status</th>
                    <th className="p-4 font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="demo-requests-table" className="divide-y divide-cloud">
                  {requests.map((r) => (
                    <tr key={r.id} data-testid={`demo-row-${r.id}`} className="hover:bg-canvas/50">
                      <td className="p-4 font-semibold text-ink">{r.child_name} <span className="text-stone font-normal">({r.child_age}y)</span></td>
                      <td className="p-4 text-stone">{r.parent_email}</td>
                      <td className="p-4">
                        <a data-testid={`demo-whatsapp-${r.id}`} href={`https://wa.me/${(r.whatsapp || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-leaf font-bold hover:underline">
                          <PhoneCall className="w-3.5 h-3.5" /> {r.whatsapp}
                        </a>
                      </td>
                      <td className="p-4 text-stone">{r.preferred_slot}</td>
                      <td className="p-4 text-stone">{r.location}</td>
                      <td className="p-4 text-stone">{r.created_at ? new Date(r.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                      <td className="p-4">
                        <span data-testid={`demo-status-${r.id}`} className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${STATUS_STYLES[r.status] || STATUS_STYLES.new}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <select
                          data-testid={`demo-status-select-${r.id}`}
                          value={r.status}
                          onChange={(e) => setStatus(r, e.target.value)}
                          className="h-9 px-2 rounded-xl border-2 border-cloud text-xs font-bold bg-white focus:border-sky focus:outline-none capitalize"
                        >
                          {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
